/**
 * Sensor Flags Editor
 *
 * Component for managing default sensor flags in a template.
 */

import { useState } from 'react';
import { Plus, X, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

interface SensorFlagsEditorProps {
  flags: string[];
  onChange: (flags: string[]) => void;
}

// Common sensor flags that can be quickly added
const COMMON_FLAGS = [
  'needs_calibration',
  'requires_review',
  'pending_verification',
  'high_priority',
  'outdoor_installation',
  'cold_storage',
  'hazardous_area',
  'difficult_access',
  'new_install',
  'replacement',
  'upgrade',
  'temporary',
];

export function SensorFlagsEditor({ flags, onChange }: SensorFlagsEditorProps) {
  const [newFlag, setNewFlag] = useState('');

  const handleAddFlag = () => {
    const flag = newFlag.trim().toLowerCase().replace(/\s+/g, '_');
    if (flag && !flags.includes(flag)) {
      onChange([...flags, flag]);
    }
    setNewFlag('');
  };

  const handleRemoveFlag = (flag: string) => {
    onChange(flags.filter((f) => f !== flag));
  };

  const handleAddCommonFlag = (flag: string) => {
    if (!flags.includes(flag)) {
      onChange([...flags, flag]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFlag();
    }
  };

  const availableCommonFlags = COMMON_FLAGS.filter((f) => !flags.includes(f));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Define default sensor flags that will be applied to all sensors in jobs using this template.
        These flags help categorize and track sensors during installation.
      </p>

      {/* Current Flags */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-2 min-h-[40px]">
            {flags.length === 0 ? (
              <p className="text-sm text-muted-foreground">No flags added yet</p>
            ) : (
              flags.map((flag) => (
                <Badge key={flag} variant="secondary" className="px-3 py-1">
                  <Flag className="h-3 w-3 mr-1" />
                  {flag}
                  <button
                    onClick={() => handleRemoveFlag(flag)}
                    className="ml-2 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Custom Flag */}
      <div className="flex gap-2">
        <Input
          value={newFlag}
          onChange={(e) => setNewFlag(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter custom flag name..."
          className="flex-1"
        />
        <Button onClick={handleAddFlag} disabled={!newFlag.trim()}>
          <Plus className="h-4 w-4 mr-2" />
          Add Flag
        </Button>
      </div>

      {/* Common Flags */}
      {availableCommonFlags.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">Quick Add Common Flags:</p>
          <div className="flex flex-wrap gap-2">
            {availableCommonFlags.map((flag) => (
              <Button
                key={flag}
                variant="outline"
                size="sm"
                onClick={() => handleAddCommonFlag(flag)}
              >
                <Plus className="h-3 w-3 mr-1" />
                {flag}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
